import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, Package, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import ProductGallery from "@/components/ProductGallery";

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // fire-and-forget view tracking (don't block UI on failure)
        api.post(`/products/${id}/view`).catch(() => {});
        return api.get(`/products?category=${r.data.category_slug}`);
      })
      .then((r) => setRelated(r.data.filter((p) => p.id !== id).slice(0, 3)))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-24 text-muted-warm lg:px-10" data-testid="product-detail-loading">
        Loading...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-24 text-center lg:px-10" data-testid="product-detail-not-found">
        <div className="font-display text-3xl font-bold text-ink">Product not found</div>
        <Link to="/products" className="mt-6 inline-block text-[#dd0447]">
          ← Back to catalog
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10" data-testid="product-detail-page">
      <Link
        to="/products"
        className="inline-flex items-center gap-2 text-sm text-muted-warm hover:text-ink"
        data-testid="product-back-link"
      >
        <ArrowLeft className="h-4 w-4" /> Back to products
      </Link>

      <div className="mt-8 grid gap-12 md:grid-cols-2">
        <div>
          <ProductGallery
            images={[product.image_url, ...(product.gallery || [])].filter(Boolean)}
            videoUrl={product.video_url}
            brand={product.brand}
            testId="product-gallery"
          />
        </div>

        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white ${product.brand === "Daikin" ? "bg-[#0097e0]" : "bg-[#dd0447]"}`}>
              {product.brand || "AREL"}
            </span>
            <div className={`overline ${product.brand === "Daikin" ? "text-[#0097e0]" : ""}`}>
              <Package className="mr-2 inline-block h-3 w-3" /> Model · {product.model || product.brand || "AREL"}
            </div>
          </div>
          <h1 className="font-display text-4xl font-black leading-tight tracking-tight text-ink md:text-5xl">
            {product.name}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-muted-warm">
            {product.description || product.short_description}
          </p>

          {product.features && product.features.length > 0 && (
            <div className="mt-8">
              <div className="overline mb-4">Key Features</div>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#dd0447]" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div className="mt-8 rounded-xl border border-line bg-[#f4f1ec] p-6">
              <div className="overline mb-4">Specifications</div>
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {Object.entries(product.specifications).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-line pb-2">
                    <dt className="text-sm text-muted-warm">{k}</dt>
                    <dd className="text-sm font-semibold text-ink text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {product.warranty && (
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-[#dd0447]/30 bg-[#fee5ec] p-5" data-testid="product-warranty-block">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#dd0447] text-white">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <div className="overline">Warranty</div>
                <div className="mt-1 text-sm font-semibold text-ink">{product.warranty}</div>
              </div>
            </div>
          )}

          <div className="mt-10 rounded-2xl border border-line bg-paper p-6" data-testid="product-inquire-block">
            <div className="mb-4 font-display text-xl font-bold text-ink">Interested in this product?</div>
            <p className="mb-6 text-sm text-muted-warm">
              Send us your details and our team will contact you with pricing,
              stock and delivery info.
            </p>
            <InquiryForm
              inquiryType="product"
              productId={product.id}
              productName={product.name}
              subject={`Inquiry: ${product.name}`}
              testIdPrefix="product-inquiry"
              compact
            />
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <div className="overline mb-4">You may also like</div>
          <h3 className="font-display text-3xl font-black tracking-tight text-ink">Related products</h3>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={`related-product-${p.id}`}
                className="arel-card group flex flex-col overflow-hidden rounded-xl"
              >
                <div className="relative aspect-square overflow-hidden bg-[#efe9df]">
                  <img src={p.image_url} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="overline">{p.model || "AREL"}</div>
                  <div className="mt-2 font-display text-lg font-bold text-ink">{p.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
